import React, { useMemo, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { api, formatApiErrorDetail } from "../lib/api";
import { useI18n } from "../lib/i18n";
import LogoBrand from "../components/LogoBrand";
import LanguageSwitch from "../components/LanguageSwitch";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Card } from "../components/ui/card";
import { toast } from "sonner";

export default function ResetPassword() {
  const { t } = useI18n();
  const nav = useNavigate();
  const [params] = useSearchParams();
  const token = useMemo(() => params.get("token") || "", [params]);
  const [pwd, setPwd] = useState("");
  const [confirm, setConfirm] = useState("");
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(false);

  const missingToken = !token;

  const submit = async (e) => {
    e.preventDefault();
    if (pwd.length < 6) { toast.error(t("auth.pwd_min6")); return; }
    if (pwd !== confirm) { toast.error(t("auth.pwd_mismatch")); return; }
    setBusy(true);
    try {
      const res = await api.post("/auth/reset-password", { token, new_password: pwd });
      setDone(true);
      toast.success(res.data?.message || t("auth.pwd_reset_ok"));
      setTimeout(() => nav("/login", { replace: true }), 1800);
    } catch (err) {
      toast.error(formatApiErrorDetail(err?.response?.data?.detail) || t("auth.something_went_wrong"));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="min-h-screen grid grid-cols-1 lg:grid-cols-2 bg-white">
      <div className="hidden lg:block relative" style={{
        backgroundImage: "url(https://images.unsplash.com/photo-1629140727571-9b5c6f6267b4?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NTY2Njl8MHwxfHNlYXJjaHwxfHxsdXh1cnklMjBob3RlbCUyMHJvb20lMjBjbGVhbnxlbnwwfHx8fDE3ODU5MTcwMDd8MA&ixlib=rb-4.1.0&q=85)",
        backgroundSize: "cover", backgroundPosition: "center",
      }}>
        <div className="absolute inset-0 bg-slate-900/40" />
      </div>
      <div className="flex flex-col items-center justify-center p-6 md:p-12">
        <div className="w-full max-w-md">
          <div className="flex justify-end mb-4">
            <div className="[&_button]:!bg-white [&_button]:!text-slate-900 [&_button]:!border-slate-300">
              <LanguageSwitch variant="light" />
            </div>
          </div>
          <div className="flex flex-col items-center mb-8"><LogoBrand variant="login" /></div>
          <Card className="p-6 md:p-8 border-slate-200 shadow-sm" data-testid="reset-password-card">
            <h1 className="font-serif text-2xl text-slate-900 mb-1">{t("auth.reset_title")}</h1>
            <p className="text-sm text-slate-500 mb-6">{t("auth.reset_help")}</p>
            {missingToken ? (
              <div className="rounded-md bg-red-50 border border-red-200 p-4 text-sm text-red-800" data-testid="reset-missing-token">
                {t("auth.reset_missing_token")}
              </div>
            ) : done ? (
              <div className="rounded-md bg-emerald-50 border border-emerald-200 p-4 text-sm text-emerald-900" data-testid="reset-success-msg">
                {t("auth.reset_success_body")}
              </div>
            ) : (
              <form onSubmit={submit} className="space-y-4">
                <div>
                  <Label htmlFor="new-pwd">{t("auth.new_password")}</Label>
                  <Input id="new-pwd" type="password" required minLength={6} value={pwd}
                    onChange={(e) => setPwd(e.target.value)}
                    className="h-11 mt-1" data-testid="reset-new-password"
                    placeholder="••••••••" />
                </div>
                <div>
                  <Label htmlFor="confirm-pwd">{t("auth.confirm_password")}</Label>
                  <Input id="confirm-pwd" type="password" required minLength={6} value={confirm}
                    onChange={(e) => setConfirm(e.target.value)}
                    className="h-11 mt-1" data-testid="reset-confirm-password"
                    placeholder="••••••••" />
                </div>
                <Button type="submit" disabled={busy}
                  className="w-full h-11 bg-slate-900 hover:bg-slate-800 text-white"
                  data-testid="reset-submit">
                  {busy ? t("auth.resetting") : t("auth.reset_action")}
                </Button>
              </form>
            )}
            <div className="mt-6 text-sm">
              <Link to="/login" className="text-slate-600 hover:text-slate-900" data-testid="reset-back-to-login">
                ← {t("auth.back_to_signin")}
              </Link>
            </div>
          </Card>
        </div>
      </div>
    </div>
  );
}
